import React, { type ReactNode, memo, useMemo } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { SelectModal } from '@components/modals/SelectModal'
import type { InputProps } from '@components/modals/SelectModal/types'
import { Loader } from '@fourthwall/components'

import * as S from './SelectProductsModal.styled'
import type { Item, Mode, SelectMode } from './SelectProductsModal.types'
import { LoadingList } from './components/LoadingList/LoadingList'
import { ProductsList } from './components/ProductsList/ProductsList'

const SCROLL_TARGET = 'scrollable-list'
const SKELETON_COUNT = 10

export type SelectProductsViewProps = {
  title: string
  items: Item[]
  inputProps: InputProps
  productsStates: any
  innerIds: string[]
  mode: Mode
  selectMode: SelectMode
  multiple?: boolean
  message?: string
  emptyMessage?: ReactNode
  isLoading: boolean
  hasNextPage: boolean
  submitButtonLabel?: string
  cancelButtonLabel?: string
  close: () => void
  onLoadMore: () => void
  onSubmitClick: () => void
  onSelect: (id: string) => (value: any) => void
  onBulkSelect: (ids: string[]) => (value: any) => void
}

export const SelectProductsModalView = memo(
  ({
    title,
    message,
    productsStates,
    innerIds = [],
    multiple = false,
    mode,
    items = [],
    selectMode,
    hasNextPage,
    inputProps,
    emptyMessage,
    isLoading,
    submitButtonLabel,
    cancelButtonLabel,
    close,
    onSubmitClick,
    onLoadMore,
    onSelect,
    onBulkSelect,
  }: SelectProductsViewProps) => {
    const renderContent = useMemo(() => {
      if (isLoading) {
        return <LoadingList count={SKELETON_COUNT} />
      }

      if (emptyMessage) {
        return <S.IdleContainer>{emptyMessage}</S.IdleContainer>
      }

      return (
        <InfiniteScroll
          dataLength={items.length}
          loader={
            <S.LoaderWrapper>
              <Loader />
            </S.LoaderWrapper>
          }
          scrollableTarget={SCROLL_TARGET}
          style={{ overflow: 'none' }}
          hasMore={hasNextPage}
          next={onLoadMore}
        >
          <ProductsList
            items={items}
            hasNextPage={hasNextPage}
            multiple={multiple}
            selectMode={selectMode}
            innerIds={innerIds}
            mode={mode}
            productsStates={productsStates}
            onSelect={onSelect}
            onBulkSelect={onBulkSelect}
          />
        </InfiniteScroll>
      )
    }, [
      isLoading,
      emptyMessage,
      items,
      hasNextPage,
      onLoadMore,
      multiple,
      selectMode,
      innerIds,
      mode,
      productsStates,
      onSelect,
      onBulkSelect,
      message,
    ])

    return (
      <SelectModal
        title={title}
        inputProps={inputProps}
        message={message}
        content={
          <S.ListContainer id={SCROLL_TARGET} $scrollable={!isLoading && !emptyMessage}>
            {renderContent}
          </S.ListContainer>
        }
        submitButtonLabel={submitButtonLabel}
        cancelButtonLabel={cancelButtonLabel}
        close={close}
        onSubmitClick={onSubmitClick}
      />
    )
  },
)

SelectProductsModalView.displayName = 'SelectProductsView'
